/**
 * interior_look.ts — what an interior's SURFACES look like (split out of BuildingInteriorModule 2026-10-01 so the look
 * and the furnishing never share a file; the look itself is the interiors programme's slice D, same day).
 *
 * THE INTERIOR-MATERIALS LAW (interiors.md law 9) holds here: every surface is UNLIT — a room reads at night whatever
 * the scene light does. "Unlit" is a property, not a class: the styled shell wears `MeshBasicNodeMaterial`s with
 * `lights = false` (fixed scene units under the exposure curve, exactly like the `MeshBasicMaterial`s before them); the
 * glass is still a `MeshBasicMaterial` NAMED `interior-glass` (the area lights, the window shadows and the
 * interior-mapping pass key on the name) and still a real opening.
 *
 * THE STYLE is data in `lib/interior_style.ts` (tradition → owner → purpose emphasis → seeded variation); this file
 * draws it:
 *  - THE STOREY ELEVATION. The shell's wall is three boxes per face (sill band, piers, header band) that share ONE
 *    material and each start their UVs at their own bottom — a canvas drawn "skirting below, field above" would paint a
 *    skirting along the bottom of the header band at 2.7 m. So the wall canvas is one full STOREY (256 px = 2.5 m across,
 *    256 px = 3.4 m up: skirting, dado with its wear at hand height, a clean field, a head rail) and the material
 *    samples it at u = the box's own UV and v = (worldY − groundY) mod 3.4 m. The rebase never moves Y, so render Y is
 *    absolute Y. A slab's underside takes a CEILING treatment by its normal; a tread's riser takes the riser colour.
 *  - COLOUR MANAGEMENT. Every canvas is tagged sRGB (the pre-programme canvases were not: their sRGB pixels were read
 *    as linear and drew washed); hex colours go through three's own sRGB → linear conversion once.
 *  - TEXTURES are small, drawn once and SHARED for the session per (tradition, purpose family, detail); the per-visit
 *    materials are the visit's (its teardown disposes every material in the group).
 *
 * THE LEVER: `localStorage atlas:interior-look = 'off'` → `legacy*` below, the pre-programme code VERBATIM (five
 * `MeshBasicMaterial`s over two untagged canvases, the slate sign in capitals, a fresh `MeshBasicMaterial({ map })` per
 * furniture clone). `verify/interior-look-bed-probe.mjs` proves it pixel-equal to a capture taken before the change.
 */
import * as THREE from 'three/webgpu';
import { Fn, texture, uv, positionWorld, normalLocal, uniform, vec2, vec3, select, fract, smoothstep, mix, fwidth } from 'three/tsl';
import type { InteriorPurpose } from '../../lib/interior_purpose.js';
import { atlas } from '../../state.js';
import { bus } from '../../bus.js';
import { settlementSites } from '../../../../shared/settlements.js';
import { civOfSettlementRowC } from '../../lib/civ_row.js';
import { activePreset } from '../../lib/quality_tiers.js';
import {
  styleFor, signText, interiorLookEnabled, roomFamilyFor, roomVolume, setActiveRoom, activeRoom, seedOf, clampDetail, mixHex, packCivOf, traditionOf,
  asTradition, playerCivIdOf, civLookEnabled,
  INTERIOR_DETAIL_RULES, REFERENCE_PALETTE, SIGN_PALETTE,
  type InteriorStyle, type Tradition, type VenueKind, type InteriorDetail, type FloorKind, type WallTreatment, type WearRule, type SignPalette,
} from '../../lib/interior_style.js';

/** One storey of the shell (building_interiors FLOOR_H — the elevation canvas spans exactly this). */
const STOREY_M = 3.4;
/** One texture tile across (the visit scales every box's UVs to ~2.5 m world tiles). */
const TILE_M = 2.5;
/** The ceiling's own tile: two world tiles across, so a vent grille comes once per 5 m, not once per panel. */
const CEIL_TILE_M = 5;
/** THE IDENTITY LINE's band in the storey elevation (metres above the storey's floor): the painter draws the
 *  tradition's line there, and a player civilisation's accent is laid over the same band as a material colour. */
const ID_LINE_Y0 = 0.928, ID_LINE_Y1 = 0.95;

/**
 * What a room's look may depend on. Stable for the whole visit. Only `id`, `key` and `purpose` are required — a bare
 * look resolves to the neutral treatment at ground height 0 (a bed building at the origin needs nothing else).
 */
export interface InteriorLook {
  id: string; key: string; purpose: InteriorPurpose;
  /** The builder's allegiance: 'a' | 'b' | '' | 'p:<civ>' (the pack prefix of `key` wins for a settlement building). */
  civ?: string | null;
  /** An explicit construction tradition (beds, previews) — outranks civ and pack. */
  tradition?: Tradition;
  /** A PLAYER CIVILISATION's chosen tradition (`player_civ_style`) and banner (`player_civ.banner`) — filled by
   *  `completeLook` when `civ` is `p:<id>`; cleared by the lever `atlas:interior-civ-look = 'off'`. */
  civTradition?: Tradition | null;
  banner?: string | null;
  /** The seeded local variation's seed (default: a hash of `id`). */
  seed?: number;
  /** The shell: floors and half extents (the room's volume — its sound), and the storey origin (absolute Y of floor 0). */
  floors?: number; hw?: number; hd?: number; groundY?: number;
  /** Where the building stands (absolute) and how it is turned — the door's place, and whose town it stands in. */
  x?: number; z?: number; yaw?: number;
  /** The player's own name for the building (always wins on the sign). */
  title?: string;
  /** The town venue this building IS, when a binding knows it (the sign then reads the canonical name). */
  venue?: VenueKind | null;
  /** The quality overlay (default: the active preset's `interiorDetail`). */
  detail?: InteriorDetail;
}

type Unlit = THREE.MeshBasicMaterial | THREE.MeshBasicNodeMaterial;
/** A partition and a rented unit's door (wave 3 — no box uses them yet). */
export interface PartitionMaterials { panel: Unlit; door: Unlit; frame: Unlit }
/** A lift: the shaft frame, the warm cab, the doors, the amber call light (wave 3 — no box uses them yet). */
export interface LiftMaterials {
  frame: Unlit; cab: Unlit; door: Unlit; call: Unlit;
  /** The call plate's RING: paint in the builder's identity (a player civilisation's banner accent). The call LIGHT
   *  (`call`) is always amber. No prop box wears it yet. */
  ring: Unlit;
}
export interface ShellMaterials {
  wall: Unlit; floor: Unlit; stair: Unlit; trim: Unlit; glass: THREE.MeshBasicMaterial;
  /** LAZY and non-enumerable: built on first read, per visit; absent under the lever (exactly today's five). */
  readonly partition?: PartitionMaterials;
  readonly lift?: LiftMaterials;
}

// ═════════════════════════════════════════════════════════════════════════════
// THE PRE-PROGRAMME LOOK — kept VERBATIM for the lever. Do not "tidy" it: the bed compares it pixel for pixel.
// ═════════════════════════════════════════════════════════════════════════════

/** A canvas nameplate — the purpose in warm trim on slate (unlit, like every interior surface). */
function legacySignTexture(text: string): THREE.CanvasTexture {
  const cv = document.createElement('canvas');
  cv.width = 512; cv.height = 128;
  const g = cv.getContext('2d')!;
  g.fillStyle = '#2b2f3a'; g.fillRect(0, 0, 512, 128);
  g.strokeStyle = '#ffd9a0'; g.lineWidth = 6; g.strokeRect(6, 6, 500, 116);
  g.fillStyle = '#ffd9a0'; g.font = '600 46px system-ui, sans-serif'; g.textAlign = 'center'; g.textBaseline = 'middle';
  g.fillText(text.toUpperCase().slice(0, 22), 256, 66);
  const t = new THREE.CanvasTexture(cv);
  t.anisotropy = 4;
  return t;
}

// ── Procedural interior textures (no asset deps; unlit per the
// interior-materials law). Near-white canvases so the material color tints
// them: warm planks underfoot, soft seamed panels on the walls. ──
let texCache: { floor: THREE.CanvasTexture; wall: THREE.CanvasTexture } | null = null;
export function interiorTextures(): { floor: THREE.CanvasTexture; wall: THREE.CanvasTexture } {
  if (texCache) return texCache;
  const make = (draw: (g: CanvasRenderingContext2D) => void): THREE.CanvasTexture => {
    const cv = document.createElement('canvas');
    cv.width = 256; cv.height = 256;
    draw(cv.getContext('2d')!);
    const t = new THREE.CanvasTexture(cv);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.anisotropy = 4;
    return t;
  };
  const floor = make((g) => {
    g.fillStyle = '#cbb79b';
    g.fillRect(0, 0, 256, 256);
    for (let row = 0; row < 4; row++) {
      const y0 = row * 64;
      const off = (row % 2) * 128;
      for (let i = -1; i < 3; i++) {
        const x0 = off + i * 128;
        const tone = 195 + ((row * 7 + i * 13) % 5) * 8;
        g.fillStyle = `rgb(${tone},${tone - 22},${tone - 48})`;
        g.fillRect(x0 + 2, y0 + 2, 124, 60);
        g.fillStyle = 'rgba(90,70,50,0.25)';
        for (let k = 0; k < 5; k++) g.fillRect(x0 + 8 + ((row * 31 + k * 47 + i * 17) % 108), y0 + 6 + (k * 11) % 50, 22, 1);
      }
    }
  });
  const wall = make((g) => {
    g.fillStyle = '#d7dbe2';
    g.fillRect(0, 0, 256, 256);
    for (let px = 0; px < 2; px++) {
      for (let py = 0; py < 2; py++) {
        const tone = 210 + ((px * 3 + py * 5) % 3) * 6;
        g.fillStyle = `rgb(${tone},${tone + 2},${tone + 8})`;
        g.fillRect(px * 128 + 3, py * 128 + 3, 122, 122);
        g.strokeStyle = 'rgba(70,80,100,0.35)';
        g.lineWidth = 2;
        g.strokeRect(px * 128 + 3, py * 128 + 3, 122, 122);
        g.fillStyle = 'rgba(120,130,150,0.5)';
        g.fillRect(px * 128 + 10, py * 128 + 10, 3, 3);
        g.fillRect(px * 128 + 115, py * 128 + 10, 3, 3);
        g.fillRect(px * 128 + 10, py * 128 + 115, 3, 3);
        g.fillRect(px * 128 + 115, py * 128 + 115, 3, 3);
      }
    }
  });
  texCache = { floor, wall };
  return texCache;
}

const MATS = {
  wall: () => new THREE.MeshBasicMaterial({ color: 0x9aa3b5, map: interiorTextures().wall }),
  floor: () => new THREE.MeshBasicMaterial({ color: 0xb0937a, map: interiorTextures().floor }),
  stair: () => new THREE.MeshBasicMaterial({ color: 0x6e5c48, map: interiorTextures().floor }),
  trim: () => new THREE.MeshBasicMaterial({ color: 0xffd9a0, toneMapped: false }),
  glass: () => Object.assign(new THREE.MeshBasicMaterial({ color: 0x9fd4ff, transparent: true, opacity: 0.14, side: THREE.DoubleSide, depthWrite: false }), { name: 'interior-glass' }), // named: the area lights find panes by material name (render wave 1)
};

function legacyShellMaterials(): ShellMaterials {
  return { wall: MATS.wall(), floor: MATS.floor(), stair: MATS.stair(), trim: MATS.trim(), glass: MATS.glass() };
}

function legacyFurnitureMaterial(src: THREE.MeshStandardMaterial): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({ map: src.map ?? null, color: 0xffffff });
}

// ═════════════════════════════════════════════════════════════════════════════
// THE STYLED LOOK
// ═════════════════════════════════════════════════════════════════════════════

function prng(seed: number): () => number {
  let a = seed >>> 0;
  return () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
function hashStr(s: string): number { return seedOf(s); }

interface CanvasEntry { tex: THREE.CanvasTexture; bytes: number; used: number }
const canvases = new Map<string, CanvasEntry>();
let useClock = 0;
/** The session's shared canvases, keyed by what was DRAWN. A cap keeps a long session from holding every combination. */
const CANVAS_CAP = 24;
function canvasFor(key: string, px: number, clampV: boolean, draw: (g: CanvasRenderingContext2D, S: number, rnd: () => number) => void): THREE.CanvasTexture {
  const hit = canvases.get(key);
  if (hit) { hit.used = ++useClock; return hit.tex; }
  const cv = document.createElement('canvas');
  cv.width = px; cv.height = px;
  const g = cv.getContext('2d')!;
  draw(g, px, prng(hashStr(key)));
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace; // COLOUR MANAGEMENT: a canvas holds sRGB pixels — say so, or they are read as linear
  t.wrapS = THREE.RepeatWrapping;
  t.wrapT = clampV ? THREE.ClampToEdgeWrapping : THREE.RepeatWrapping;
  t.anisotropy = 4;
  t.name = `interior:${key}`;
  canvases.set(key, { tex: t, bytes: Math.round(px * px * 4 * 1.34), used: ++useClock });
  if (canvases.size > CANVAS_CAP) {
    // evict the least recently used (never one the room being built just asked for — it holds the newest stamps)
    let oldK = '', oldU = Infinity;
    for (const [k, e] of canvases) if (e.used < oldU) { oldU = e.used; oldK = k; }
    const old = canvases.get(oldK);
    if (old && oldK !== key) { canvases.delete(oldK); old.tex.dispose(); }
  }
  return t;
}

/** Fill a rect given in METRES of the storey elevation (x across the 2.5 m tile, y0..y1 up from the floor), wrapping across the tile. */
function elevationPainter(g: CanvasRenderingContext2D, S: number): {
  X: (m: number) => number; Y: (m: number) => number;
  band: (y0: number, y1: number, fill: string, alpha?: number) => void;
  box: (x: number, w: number, y0: number, y1: number, fill: string, alpha?: number) => void;
  hline: (y: number, px: number, fill: string, alpha?: number) => void;
  blob: (x: number, y: number, rx: number, ry: number, fill: string, alpha: number) => void;
} {
  const X = (m: number): number => (m * S) / TILE_M;
  const Y = (m: number): number => S - (m * S) / STOREY_M;
  const set = (fill: string, alpha = 1): void => { g.globalAlpha = alpha; g.fillStyle = fill; };
  const band = (y0: number, y1: number, fill: string, alpha = 1): void => { set(fill, alpha); const a = Math.round(Y(y1)), b = Math.round(Y(y0)); g.fillRect(0, a, S, Math.max(1, b - a)); g.globalAlpha = 1; };
  const box = (x: number, w: number, y0: number, y1: number, fill: string, alpha = 1): void => {
    set(fill, alpha);
    const a = Math.round(Y(y1)), b = Math.round(Y(y0)), px = Math.round(X(x)), pw = Math.max(1, Math.round(X(w)));
    for (const off of [-S, 0, S]) g.fillRect(px + off, a, pw, Math.max(1, b - a));
    g.globalAlpha = 1;
  };
  const hline = (y: number, px: number, fill: string, alpha = 1): void => { set(fill, alpha); g.fillRect(0, Math.round(Y(y)) - Math.floor(px / 2), S, Math.max(1, px)); g.globalAlpha = 1; };
  const blob = (x: number, y: number, rx: number, ry: number, fill: string, alpha: number): void => {
    for (const off of [-S, 0, S]) {
      const cx = X(x) + off, cy = Y(y), w = X(rx), h = (ry * S) / STOREY_M;
      const grd = g.createRadialGradient(cx, cy, 0, cx, cy, 1);
      grd.addColorStop(0, fill); grd.addColorStop(1, 'rgba(0,0,0,0)');
      g.save(); g.translate(cx, cy); g.scale(w, h); g.translate(-cx, -cy);
      g.globalAlpha = alpha; g.fillStyle = grd; g.beginPath(); g.arc(cx, cy, 1, 0, Math.PI * 2); g.fill();
      g.restore();
    }
    g.globalAlpha = 1;
  };
  return { X, Y, band, box, hline, blob };
}

/**
 * THE WALL ELEVATION: one storey, floor (canvas bottom) to ceiling (top). Bottom → top: skirting 0–0.12 m · dado to
 * 0.96 (the band hands, boots and chair backs reach) · the identity line and the cap rail to 1.02 (= the window sill)
 * · the clean field · the head rail at 2.7 (= the window head) · the frieze to the ceiling. The shading is
 * NONDIRECTIONAL (a contact gradient at the floor and under the ceiling, symmetric seam shadows): no sun is painted
 * into a room whose day moves. Wear sits ONLY at the base and at hand height, and never above 1.35 m.
 */
function drawWallElevation(g: CanvasRenderingContext2D, S: number, rnd: () => number, w: WallTreatment, wear: WearRule, strip: string, detail: InteriorDetail): void {
  const p = elevationPainter(g, S);
  const k = S / 256; // line weights scale with the canvas
  const rich = INTERIOR_DETAIL_RULES[detail].wear;
  // the field, and the frieze over the head rail
  p.band(0, STOREY_M, w.field);
  p.band(2.72, STOREY_M, mixHex(w.field, w.frame, 0.08));
  // panels: a per-panel tone (hand-built, never uniform), then the seams with a symmetric contact shadow
  const n = Math.max(1, Math.round(TILE_M / w.panelM));
  const seamPx = Math.max(1, Math.round(p.X(w.seamM)));
  const seamAlpha = Math.min(1, Math.max(0.5, 0.45 + w.seamM * 18));
  for (let i = 0; i < n; i++) {
    const x = (i * TILE_M) / n;
    if (rich) { const d = rnd() * 2 - 1; p.box(x, TILE_M / n, 1.02, 2.68, d > 0 ? '#ffffff' : '#000000', Math.abs(d) * 0.03); }
    if (w.pilaster && i === 0) continue; // the pilaster stands where this seam would be
    g.globalAlpha = 0.1; g.fillStyle = '#000000';
    for (const off of [-S, 0, S]) g.fillRect(Math.round(p.X(x)) + off - Math.ceil(seamPx / 2) - 1, Math.round(p.Y(2.68)), seamPx + 2, Math.round(p.Y(1.02) - p.Y(2.68)));
    g.globalAlpha = seamAlpha; g.fillStyle = w.frame;
    for (const off of [-S, 0, S]) g.fillRect(Math.round(p.X(x)) + off - Math.floor(seamPx / 2), Math.round(p.Y(2.68)), seamPx, Math.round(p.Y(1.02) - p.Y(2.68)));
    g.globalAlpha = 1;
  }
  if (w.pilaster) {
    // the Aurelian order: a lighter pilaster at each bay with a thin gilt line down both edges, a base and a capital
    const pw = 0.3;
    p.box(-pw / 2, pw, 1.02, 2.68, mixHex(w.field, '#ffffff', 0.38));
    p.box(-pw / 2 - 0.035, pw + 0.07, 1.02, 1.13, mixHex(w.field, '#ffffff', 0.22));
    p.box(-pw / 2 - 0.035, pw + 0.07, 2.55, 2.68, mixHex(w.field, '#ffffff', 0.22));
    const line = Math.max(1, Math.round(1.2 * k));
    g.globalAlpha = 0.9; g.fillStyle = w.capLine;
    for (const off of [-S, 0, S]) for (const ex of [-pw / 2, pw / 2]) g.fillRect(Math.round(p.X(ex)) + off - Math.floor(line / 2), Math.round(p.Y(2.55)), line, Math.round(p.Y(1.13) - p.Y(2.55)));
    g.globalAlpha = 1;
  }
  if (w.rivets) {
    // insulated / service panels: a mid rail and rivets at the panel corners (plain dark dots — no bevel, no light side)
    p.hline(1.84, Math.max(1, Math.round(2 * k)), w.frame, 0.85);
    if (rich) {
      const r = Math.max(1, Math.round(1.6 * k));
      g.fillStyle = mixHex(w.frame, '#000000', 0.25); g.globalAlpha = 0.9;
      for (let i = 0; i < n; i++) for (const side of [-1, 1]) for (const y of [1.12, 1.74, 1.94, 2.58]) {
        const cx = Math.round(p.X((i * TILE_M) / n)) + side * (Math.ceil(seamPx / 2) + Math.round(3 * k));
        for (const off of [-S, 0, S]) g.fillRect(cx + off - Math.floor(r / 2), Math.round(p.Y(y)) - Math.floor(r / 2), r, r);
      }
      g.globalAlpha = 1;
    }
  }
  // THE HABITATION LINE: one thin warm strip light in the frieze, just over the window head — amber means people
  // live here, in BOTH civilisations. One line on every storey; nothing else on the wall glows.
  { const y0 = 2.755, y1 = 2.79;
    const halo = g.createLinearGradient(0, p.Y(y1 + 0.09), 0, p.Y(y0 - 0.09));
    halo.addColorStop(0, 'rgba(255,180,94,0)'); halo.addColorStop(0.5, 'rgba(255,180,94,0.2)'); halo.addColorStop(1, 'rgba(255,180,94,0)');
    g.fillStyle = halo; g.fillRect(0, p.Y(y1 + 0.09), S, p.Y(y0 - 0.09) - p.Y(y1 + 0.09));
    p.band(y0, y1, strip); }
  // the head rail (the window head line) and the contact shadow under the ceiling
  p.band(2.68, 2.72, w.frame);
  p.hline(2.665, Math.max(1, Math.round(1.5 * k)), '#000000', 0.12);
  { const grd = g.createLinearGradient(0, p.Y(STOREY_M), 0, p.Y(3.0)); grd.addColorStop(0, 'rgba(0,0,0,0.13)'); grd.addColorStop(1, 'rgba(0,0,0,0)'); g.fillStyle = grd; g.fillRect(0, p.Y(STOREY_M), S, p.Y(3.0) - p.Y(STOREY_M)); }
  // the dado
  p.band(0.12, 0.96, w.dado);
  const jointCol = mixHex(w.dado, '#000000', 0.3);
  const jpx = Math.max(1, Math.round(1.2 * k));
  if (w.dadoPattern === 'blocks') {
    const courses = [0.12, 0.4, 0.68, 0.96];
    for (let c = 0; c < 3; c++) {
      const y0 = courses[c], y1 = courses[c + 1], bw = TILE_M / 4, shift = (c % 2) * (bw / 2);
      for (let i = 0; i < 4; i++) {
        const x = i * bw + shift;
        if (rich) { const d = rnd() * 2 - 1; p.box(x, bw, y0, y1, d > 0 ? '#ffffff' : '#000000', Math.abs(d) * 0.055); }
        g.globalAlpha = 0.85; g.fillStyle = jointCol;
        for (const off of [-S, 0, S]) g.fillRect(Math.round(p.X(x)) + off, Math.round(p.Y(y1)), jpx, Math.round(p.Y(y0) - p.Y(y1)));
        g.globalAlpha = 1;
      }
      if (c > 0) p.hline(y0, jpx, jointCol, 0.85);
    }
  } else if (w.dadoPattern === 'boards') {
    const nb = 16, bw = TILE_M / nb;
    for (let i = 0; i < nb; i++) {
      const x = i * bw;
      if (rich) {
        const d = rnd() * 2 - 1;
        p.box(x, bw, 0.12, 0.96, d > 0 ? '#ffffff' : '#000000', Math.abs(d) * 0.07);
        for (let s = 0; s < 2; s++) p.box(x + bw * (0.2 + rnd() * 0.6), 0.004, 0.14 + rnd() * 0.2, 0.5 + rnd() * 0.44, '#000000', 0.07);
      }
      g.globalAlpha = 0.75; g.fillStyle = jointCol;
      for (const off of [-S, 0, S]) g.fillRect(Math.round(p.X(x)) + off, Math.round(p.Y(0.96)), Math.max(1, Math.round(k)), Math.round(p.Y(0.12) - p.Y(0.96)));
      g.globalAlpha = 1;
    }
  } else {
    if (w.dadoPattern === 'kick') { p.band(0.12, 0.34, mixHex(w.dado, w.frame, 0.38)); p.hline(0.34, jpx, w.frame, 0.7); }
    for (let i = 0; i < n; i++) {
      g.globalAlpha = w.dadoPattern === 'plain' ? 0.28 : 0.6; g.fillStyle = w.frame;
      for (const off of [-S, 0, S]) g.fillRect(Math.round(p.X((i * TILE_M) / n)) + off, Math.round(p.Y(0.96)), Math.max(1, Math.round(k)), Math.round(p.Y(0.12) - p.Y(0.96)));
      g.globalAlpha = 1;
    }
  }
  // the identity line, the cap rail (the sill's own row is the frame), the skirting
  p.band(ID_LINE_Y0, ID_LINE_Y1, w.capLine); // ~2 cm: an identity LINE, never a painted band
  p.band(0.955, 1.03, w.frame);
  p.band(0, 0.12, w.skirting);
  p.hline(0.12, Math.max(1, Math.round(k)), mixHex(w.skirting, '#000000', 0.35), 0.9);
  // the contact gradient at the floor (restrained, all round — not a shadow from anywhere)
  { const a = 0.1 + 0.08 * wear.base; const grd = g.createLinearGradient(0, p.Y(0), 0, p.Y(0.55)); grd.addColorStop(0, `rgba(0,0,0,${a.toFixed(3)})`); grd.addColorStop(1, 'rgba(0,0,0,0)'); g.fillStyle = grd; g.fillRect(0, p.Y(0.55), S, p.Y(0) - p.Y(0.55)); }
  if (!rich) return;
  // ── WEAR: dust and scuffs at the base, rubs and scuffs at hand height. Nothing above 1.35 m. ──
  const dust = REFERENCE_PALETTE.dust;
  for (let i = 0, N = Math.round(9 * wear.base); i < N; i++) p.blob(rnd() * TILE_M, 0.1 + rnd() * 0.2, 0.1 + rnd() * 0.28, 0.035 + rnd() * 0.06, dust, 0.14 + rnd() * 0.1);
  for (let i = 0, N = Math.round(16 * wear.base); i < N; i++) { const y = 0.02 + rnd() * 0.3; const dark = rnd() < 0.75; p.box(rnd() * TILE_M, 0.05 + rnd() * 0.2, y, y + 0.012, dark ? '#000000' : '#ffffff', dark ? 0.1 + rnd() * 0.1 : 0.05 + rnd() * 0.04); }
  for (let i = 0, N = Math.round(8 * wear.hand); i < N; i++) p.blob(rnd() * TILE_M, 0.86 + rnd() * 0.38, 0.12 + rnd() * 0.3, 0.035 + rnd() * 0.05, rnd() < 0.5 ? '#ffffff' : '#000000', 0.06 + rnd() * 0.05);
  for (let i = 0, N = Math.round(18 * wear.hand); i < N; i++) {
    const hi = rnd() < 0.35, y = hi ? 1.04 + rnd() * 0.28 : 0.72 + rnd() * 0.2;
    p.box(rnd() * TILE_M, 0.04 + rnd() * 0.18, y, y + 0.011, '#000000', 0.09 + rnd() * 0.09);
  }
  for (let i = 0, N = Math.round(6 * wear.hand); i < N; i++) p.box(rnd() * TILE_M, 0.1 + rnd() * 0.22, 0.985, 1.025, '#ffffff', 0.07 + rnd() * 0.06); // the rail polished where hands go
}

/** A FLOOR pattern, near-white so the material's colour tints it (shared by every tradition that lays this kind). */
function drawFloorPattern(g: CanvasRenderingContext2D, S: number, rnd: () => number, kind: FloorKind, wear: number, rich: boolean): void {
  const k = S / 256;
  const grey = (v: number): string => { const c = Math.round(v); return `rgb(${c},${c},${c})`; };
  g.fillStyle = grey(236); g.fillRect(0, 0, S, S);
  const wrapRect = (x: number, y: number, w: number, h: number): void => { for (const ox of [-S, 0, S]) for (const oy of [-S, 0, S]) g.fillRect(Math.round(x) + ox, Math.round(y) + oy, Math.max(1, Math.round(w)), Math.max(1, Math.round(h))); };
  if (kind === 'plank') {
    const rows = 8, rh = S / rows;
    for (let r = 0; r < rows; r++) {
      const shift = ((r * 0.37) % 1) * S;
      for (let b = 0; b < 2; b++) {
        const x = shift + (b * S) / 2;
        g.globalAlpha = 1; g.fillStyle = grey(rich ? 222 + rnd() * 26 : 234);
        wrapRect(x, r * rh, S / 2, rh);
        if (rich) { g.fillStyle = '#000000'; for (let s = 0; s < 5; s++) { g.globalAlpha = 0.045 + rnd() * 0.04; wrapRect(x + rnd() * (S / 2 - 30 * k), r * rh + 3 * k + rnd() * (rh - 6 * k), (18 + rnd() * 50) * k, Math.max(1, k * 0.8)); } }
        g.globalAlpha = 0.6; g.fillStyle = grey(96);
        wrapRect(x, r * rh, Math.max(1, k), rh); // the butt joint
      }
      g.globalAlpha = 0.72; g.fillStyle = grey(86);
      g.fillRect(0, Math.round(r * rh), S, Math.max(1, Math.round(k)));
    }
  } else if (kind === 'stone') {
    const rows = 4, rh = S / rows;
    for (let r = 0; r < rows; r++) {
      const shift = (r % 2) * (S / 4);
      for (let b = 0; b < 2; b++) {
        const x = shift + (b * S) / 2;
        g.globalAlpha = 1; g.fillStyle = grey(rich ? 228 + rnd() * 20 : 238);
        wrapRect(x, r * rh, S / 2, rh);
        g.globalAlpha = 0.7; g.fillStyle = grey(112);
        wrapRect(x, r * rh, Math.max(1, Math.round(1.6 * k)), rh);
      }
      g.globalAlpha = 0.7; g.fillStyle = grey(112);
      g.fillRect(0, Math.round(r * rh), S, Math.max(1, Math.round(1.6 * k)));
    }
    if (rich) for (let i = 0; i < 40; i++) { g.globalAlpha = 0.012 + rnd() * 0.016; g.fillStyle = rnd() < 0.5 ? '#000000' : '#ffffff'; const r = (6 + rnd() * 22) * k; g.beginPath(); g.arc(rnd() * S, rnd() * S, r, 0, Math.PI * 2); g.fill(); }
  } else {
    for (let r = 0; r < 2; r++) for (let b = 0; b < 2; b++) {
      g.globalAlpha = 1; g.fillStyle = grey(rich ? 232 + rnd() * 14 : 238);
      g.fillRect((b * S) / 2, (r * S) / 2, S / 2, S / 2);
      g.globalAlpha = 0.55; g.fillStyle = grey(110);
      g.fillRect((b * S) / 2, (r * S) / 2, S / 2, Math.max(1, Math.round(k)));
      g.fillRect((b * S) / 2, (r * S) / 2, Math.max(1, Math.round(k)), S / 2);
    }
  }
  if (rich && wear > 0) {
    // traffic: scuffs and a few dusty patches, everywhere a little (a tile cannot know where the path is)
    g.fillStyle = '#000000';
    for (let i = 0, N = Math.round(26 * wear); i < N; i++) { g.globalAlpha = 0.04 + rnd() * 0.05; wrapRect(rnd() * S, rnd() * S, (6 + rnd() * 26) * k, Math.max(1, k)); }
    g.fillStyle = '#ffffff';
    for (let i = 0, N = Math.round(7 * wear); i < N; i++) { g.globalAlpha = 0.035 + rnd() * 0.03; const r = (10 + rnd() * 26) * k; g.beginPath(); g.arc(rnd() * S, rnd() * S, r, 0, Math.PI * 2); g.fill(); }
  }
  g.globalAlpha = 1;
}

/** A CEILING pattern over a 5 m tile, near-white (tinted by the ceiling field): panels, beams where the tradition frames heavily, ONE vent grille. */
function drawCeilingPattern(g: CanvasRenderingContext2D, S: number, _rnd: () => number, panelM: number, beams: boolean, rich: boolean): void {
  const k = S / 256;
  g.fillStyle = 'rgb(243,243,243)'; g.fillRect(0, 0, S, S);
  const n = Math.max(1, Math.round(CEIL_TILE_M / panelM));
  g.globalAlpha = 0.5; g.fillStyle = 'rgb(150,150,150)';
  for (let i = 0; i < n; i++) { g.fillRect(Math.round((i * S) / n), 0, Math.max(1, Math.round(k * 0.8)), S); g.fillRect(0, Math.round((i * S) / n), S, Math.max(1, Math.round(k * 0.8))); }
  if (beams) {
    // exposed framing (the Borean bay): a beam at every other panel line — structure, a step darker, never a black bar
    g.globalAlpha = 1; g.fillStyle = 'rgb(172,176,182)';
    const bw = Math.max(2, Math.round((0.14 * S) / CEIL_TILE_M));
    for (let i = 0; i < n; i += 2) for (const off of [0, S]) g.fillRect(Math.round((i * S) / n) + off - Math.floor(bw / 2), 0, bw, S);
  }
  if (rich) {
    // one vent grille per 5 m: the room breathes (its sound is ventilation, never a crowd)
    const gx = Math.round(S * 0.56), gy = Math.round(S * 0.18), gw = Math.round((0.62 * S) / CEIL_TILE_M), gh = Math.round((0.22 * S) / CEIL_TILE_M);
    g.globalAlpha = 0.75; g.fillStyle = 'rgb(128,130,134)'; g.fillRect(gx, gy, gw, gh);
    g.globalAlpha = 0.85; g.fillStyle = 'rgb(70,72,76)';
    for (let sl = 0; sl < 3; sl++) g.fillRect(gx + Math.max(1, Math.round(k)), gy + Math.round(((sl + 0.5) * gh) / 3), gw - 2 * Math.max(1, Math.round(k)), Math.max(1, Math.round(k * 0.8)));
  }
  g.globalAlpha = 1;
}

const wearStep = (w: number): number => (w <= 0 ? 0 : w < 0.65 ? 1 : 2);
function wallTextureFor(st: InteriorStyle, detail: InteriorDetail): THREE.CanvasTexture {
  const px = INTERIOR_DETAIL_RULES[detail].texturePx;
  return canvasFor(`wall|${st.signature}|${detail}`, px, true, (g, S, rnd) => drawWallElevation(g, S, rnd, st.wall, st.wear, mixHex(st.trim.strip, '#ffffff', 0.25), detail));
}
function floorTextureFor(kind: FloorKind, wear: number, detail: InteriorDetail): THREE.CanvasTexture {
  const rules = INTERIOR_DETAIL_RULES[detail];
  return canvasFor(`floor|${kind}|${wearStep(wear)}|${detail}`, rules.texturePx, false, (g, S, rnd) => drawFloorPattern(g, S, rnd, kind, [0, 0.55, 1][wearStep(wear)], rules.wear));
}
function ceilingTextureFor(st: InteriorStyle, detail: InteriorDetail): THREE.CanvasTexture {
  const rules = INTERIOR_DETAIL_RULES[detail];
  return canvasFor(`ceil|${st.ceiling.panelM}|${st.ceiling.beams ? 1 : 0}|${detail}`, rules.texturePx, false, (g, S, rnd) => drawCeilingPattern(g, S, rnd, st.ceiling.panelM, st.ceiling.beams, rules.wear));
}

/** An unlit node material: no light, no environment, no fog — one display value at noon and at midnight (law 9). */
function unlit(colorNode: unknown, name: string): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial();
  m.colorNode = colorNode as never;
  m.lights = false;
  m.fog = false; // a sandstorm's fog must not haze a room; the street through the panes keeps it
  m.name = name;
  return m;
}
const lin = (hex: string): THREE.Color => new THREE.Color(hex); // sRGB hex → the linear working space, once
/** Height above this storey's floor as a 0..1 fraction (the elevation canvas's v). */
const storeyV = (baseY: ReturnType<typeof uniform>): ReturnType<typeof fract> => fract(positionWorld.y.sub(baseY).div(STOREY_M).sub(0.0001));

let lastVisit: { signature: string; detail: InteriorDetail; textures: number; materials: number; accent: string | null } | null = null;

/** The partition's skirting height and how far its wainscot is mixed toward the frame tone (slice H; decided with pictures). */
export const PARTITION_SKIRT_M = 0.12;
export const PARTITION_WAINSCOT_MIX = 0.3;
function partitionTrimOn(): boolean {
  try { return typeof localStorage === 'undefined' || localStorage.getItem('atlas:interior-partition-trim') !== 'off'; } catch { return true; }
}
function styledShellMaterials(look: InteriorLook): ShellMaterials {
  const st = styleFor(look);
  const detail = clampDetail(look.detail ?? activePreset().interiorDetail);
  const baseY = uniform(look.groundY ?? 0);
  const value = st.variation.value;
  const wallTex = wallTextureFor(st, detail);
  const floorTex = floorTextureFor(st.floor.kind, st.wear.base, detail);
  const ceilTex = ceilingTextureFor(st, detail);
  const treadTex = floorTextureFor(st.stair.kind, st.wear.base, detail);
  const tint = (hex: string, k = 1): ReturnType<typeof uniform> => uniform(lin(hex).multiplyScalar(k));

  // THE LAZY-SAMPLE LAW: every sample that feeds a select() is taken at the top of its Fn.
  // THE BUILD-TIME LAW: which wall this is (plain, or with a player civilisation's accent line) is decided HERE, not
  // inside a Fn. The accent is a MATERIAL colour laid over the identity line's band of the shared canvas — the canvas
  // (cached per tradition, family, detail) keeps the tradition's own line and is never re-drawn for a banner.
  const wall = unlit(st.wallLine
    ? Fn(() => {
      const s = texture(wallTex, vec2(uv().x.add(st.variation.phase), storeyV(baseY))).rgb.toVar();
      const h = storeyV(baseY).mul(STOREY_M);
      // the pixel's footprint in metres of wall height, from the UNWRAPPED height (fract() jumps at every storey)
      const w = fwidth(positionWorld.y).clamp(0.0005, 0.06);
      const lineW = ID_LINE_Y1 - ID_LINE_Y0;
      const cover = smoothstep(uniform(ID_LINE_Y0).sub(w), uniform(ID_LINE_Y0), h)
        .mul(smoothstep(uniform(ID_LINE_Y1), uniform(ID_LINE_Y1).add(w), h).oneMinus()) // never smoothstep(hi, lo): undefined in GLSL
        .mul(uniform(lineW).div(w.add(lineW)).mul(2).min(1)); // a far line thins out, as a mip would
      return mix(s.mul(uniform(value)), tint(st.wallLine as string), cover); // the function form (THE MIX LAW)
    })()
    : Fn(() => {
      const s = texture(wallTex, vec2(uv().x.add(st.variation.phase), storeyV(baseY))).rgb.toVar();
      return s.mul(uniform(value));
    })(), 'interior-wall');

  const floor = unlit(Fn(() => {
    const top = texture(floorTex, uv()).rgb.mul(tint(st.floor.base, value)).toVar();
    const under = texture(ceilTex, uv().mul(TILE_M / CEIL_TILE_M)).rgb.mul(tint(st.ceiling.field)).toVar();
    const edge = tint(mixHex(st.ceiling.field, st.wall.frame, 0.3));
    const ny = normalLocal.y;
    return select(ny.greaterThan(0.5), top, select(ny.lessThan(-0.5), under, edge));
  })(), 'interior-floor');

  const stair = unlit(Fn(() => {
    const tread = texture(treadTex, uv()).rgb.mul(tint(st.stair.tread)).toVar();
    return select(normalLocal.y.greaterThan(0.5), tread, tint(st.stair.riser));
  })(), 'interior-stair');

  // TRIM: rails, columns, shelves are the frame material with a worn metal cap at hand-rail height; the thin strip
  // under the ceiling is the amber light (habitation, in both civilisations). One class, told apart by storey height.
  const trim = unlit(Fn(() => {
    const h = storeyV(baseY).mul(STOREY_M);
    const body = tint(st.trim.body).mul(smoothstep(-0.1, 0.45, h).mul(0.16).add(0.84));
    const isStrip = h.greaterThan(3.15).and(h.lessThan(3.29)); // the thin strip under a top storey's ceiling: a cornice moulding
    const isCap = h.greaterThan(0.985).and(h.lessThan(1.065));
    // above the cap (a full-height guard beside a stair run, a column's shaft) the frame material would be a dark slab
    // the size of a wall: it is the wall's own field there, a shade down — the rail and its cap stay the frame's
    const upper = tint(mixHex(st.wall.field, st.trim.body, 0.1), value);
    return select(isStrip, tint(mixHex(st.wall.frame, st.wall.field, 0.35)), select(isCap, tint(st.trim.cap), select(h.greaterThan(1.065), upper, body)));
  })(), 'interior-trim');

  const glass = Object.assign(new THREE.MeshBasicMaterial({ color: st.glass.color, transparent: true, opacity: st.glass.opacity, side: THREE.DoubleSide, depthWrite: false }), { name: 'interior-glass' }); // the NAME is a contract: area lights, window shadows

  const mats = { wall, floor, stair, trim, glass } as ShellMaterials;
  // Wave 3's surfaces (no box uses them yet): LAZY, so a visit that never asks pays nothing; non-enumerable, so code
  // that walks the five does not build them by accident.
  let partition: PartitionMaterials | null = null, lift: LiftMaterials | null = null;
  const flatAO = (hex: string, name: string, cap?: string): THREE.MeshBasicNodeMaterial => unlit(Fn(() => {
    const h = storeyV(baseY).mul(STOREY_M);
    const body = tint(hex).mul(smoothstep(-0.1, 0.45, h).mul(0.14).add(0.86));
    return cap ? select(h.greaterThan(0.94).and(h.lessThan(1.0)), tint(cap), body) : body;
  })(), name);
  Object.defineProperty(mats, 'partition', {
    enumerable: false, configurable: true,
    get: (): PartitionMaterials => (partition ??= {
      // THE PARTITION'S WALL HIERARCHY (slice H, 2026-10-02): a partition read as a flat pale wall beside the shell's
      // wainscoted ones. It wears the same order now — the shell's skirting, the shell's dado tone below the rail, the
      // wall's identity line as the dado rail, its own partition field above — so one room has one wall order.
      // `atlas:interior-partition-trim = 'off'` → the flat panel with the line (the look of 2026-10-01).
      panel: partitionTrimOn() ? unlit(Fn(() => {
        const h = storeyV(baseY).mul(STOREY_M);
        const field = tint(st.partition.panel).mul(smoothstep(-0.1, 0.45, h).mul(0.14).add(0.86));
        // the lower band is the SHELL's own dado tone carried onto the partition (one room, one wall order), a share
        // toward the partition's panel; the skirting the shell's own skirting
        const wains = tint(mixHex(st.wall.dado, st.partition.panel, PARTITION_WAINSCOT_MIX));
        const skirt = tint(st.wall.skirting);
        const rail = tint(st.wallLine ?? st.wall.capLine);
        return select(h.lessThan(PARTITION_SKIRT_M), skirt, select(h.greaterThan(0.94).and(h.lessThan(1.0)), rail, select(h.lessThan(0.94), wains, field)));
      })(), 'interior-partition') : flatAO(st.partition.panel, 'interior-partition', st.wallLine ?? st.wall.capLine),
      // a unit door: the tradition's wood over a kick plate; its number plate is signTexture(text, look, { kind: 'plate' })
      door: unlit(Fn(() => {
        const wood = texture(floorTextureFor('plank', 0.5, detail), uv()).rgb.mul(tint(st.partition.door)).toVar();
        const h = storeyV(baseY).mul(STOREY_M);
        return select(h.lessThan(0.3), tint(mixHex(st.partition.door, st.partition.frame, 0.55)), wood);
      })(), 'interior-unit-door'),
      frame: unlit(vec3(tint(st.partition.frame)), 'interior-partition-frame'),
    }),
  });
  Object.defineProperty(mats, 'lift', {
    enumerable: false, configurable: true,
    get: (): LiftMaterials => (lift ??= {
      frame: unlit(vec3(tint(st.lift.frame)), 'interior-lift-frame'),
      cab: flatAO(st.lift.cab, 'interior-lift-cab', st.trim.cap),
      // the doors: brushed alloy with a centre seam (uv.x = 0.5 on an untiled door leaf pair)
      door: unlit(Fn(() => {
        const seam = smoothstep(0.0, 0.006, uv().x.sub(0.5).abs());
        return tint(st.lift.door).mul(seam.mul(0.45).add(0.55));
      })(), 'interior-lift-door'),
      call: unlit(vec3(tint(st.lift.call, 1.35)), 'interior-lift-call'),
      ring: unlit(vec3(tint(st.lift.ring)), 'interior-lift-ring'),
    }),
  });
  lastVisit = { signature: st.signature, detail, textures: new Set([wallTex, floorTex, ceilTex, treadTex]).size, materials: 5, accent: st.accent?.paint ?? null };
  return mats;
}

/** The shell's five materials for one visit (fresh instances — the visit disposes them with its group). */
export function shellMaterials(look: InteriorLook): ShellMaterials {
  completeLook(look); // who built it, the seed, the room for the audio — once per look; under the lever it only clears the room
  if (!interiorLookEnabled()) return legacyShellMaterials();
  return styledShellMaterials(look);
}

// ─── the sign ────────────────────────────────────────────────────────────────

export interface SignOpts {
  /** 'sign' = the nameplate inside the door (512 × 128); 'plate' = a unit door's number plate (256 × 128). */
  kind?: 'sign' | 'plate';
}
/**
 * The nameplate inside the door, and (kind 'plate') a unit door's number plate. Dynamic text, never baked into a
 * mesh: the builder's sign palette (style.md §4.1 ink on paper), SENTENCE case; the player's own name for the building
 * wins, then the canonical venue name where the building is known to BE that venue, then the purpose.
 */
export function signTexture(text: string, look?: InteriorLook, opts: SignOpts = {}): THREE.CanvasTexture {
  if (!interiorLookEnabled()) return legacySignTexture(text);
  if (look) completeLook(look);
  const plate = opts.kind === 'plate';
  const st = look ? styleFor(look) : null;
  // ink on paper is the TRADITION's (style.md §4.1); a player civilisation's banner takes only the board's frame
  const pal: SignPalette = st ? (plate ? st.partition.plate : st.sign) : SIGN_PALETTE.neutral;
  const shown = plate ? signText(text) : signText(text, { title: look?.title, venue: look?.venue ?? null, civ: look?.civ ?? packCivOf(look?.key ?? '') });
  const W = plate ? 256 : 512, H = 128;
  const cv = document.createElement('canvas');
  cv.width = W; cv.height = H;
  const g = cv.getContext('2d')!;
  g.fillStyle = pal.paper; g.fillRect(0, 0, W, H);
  // a plank, like the town's own signs: an ink rule inside the edge, and the board's own edge a shade darker
  const fw = pal.frame ? 10 : 6; // the accent frame is the civilisation's identity on the board: a little bolder
  g.strokeStyle = pal.frame ?? mixHex(pal.paper, pal.ink, 0.35); g.lineWidth = fw; g.strokeRect(fw / 2, fw / 2, W - fw, H - fw);
  g.strokeStyle = pal.ink; g.lineWidth = 3; g.strokeRect(fw + 5.5, fw + 5.5, W - 2 * fw - 11, H - 2 * fw - 11);
  g.fillStyle = pal.ink; g.textAlign = 'center'; g.textBaseline = 'middle';
  let size = plate ? 62 : 54;
  const font = (px: number): string => `bold ${px}px Georgia, 'Times New Roman', serif`;
  g.font = font(size);
  while (size > 24 && g.measureText(shown).width > W - 56) { size -= 2; g.font = font(size); }
  g.fillText(shown, W / 2, H / 2 + 3);
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  t.userData.text = shown;
  t.userData.frame = pal.frame ?? null;
  return t;
}

// ─── the furniture adapter ───────────────────────────────────────────────────

interface SharedMat { mat: Unlit; refs: number }
const furnCache = new WeakMap<InteriorLook, Map<string, SharedMat>>();
let furnStats = { calls: 0, built: 0, emissive: 0 };

/** The unlit twin of ONE cooked source material: its colour map (and the map's own transform), side and alpha; its emissive map ADDED so a lamp shade or a screen reads lit. */
function styledFurnitureMaterial(src: THREE.MeshStandardMaterial, emissiveOn: boolean): Unlit {
  const alpha = { side: src.side, transparent: src.transparent === true, opacity: src.opacity ?? 1, alphaTest: src.alphaTest ?? 0, alphaMap: src.alphaMap ?? null };
  const em = emissiveOn && src.emissiveMap ? src.emissiveMap : null;
  if (!em) {
    const m = new THREE.MeshBasicMaterial({ map: src.map ?? null, color: 0xffffff, ...alpha });
    m.fog = false;
    return m;
  }
  // The emissive map rides three's own material path (`materialEmissive`: emissive × intensity × map, the map's
  // transform honoured) on an unlit node material — outgoing = the colour map + the indicator's light.
  const m = new THREE.MeshBasicNodeMaterial();
  const mm = m as unknown as THREE.MeshBasicMaterial & { emissive: THREE.Color; emissiveMap: THREE.Texture | null; emissiveIntensity: number };
  mm.map = src.map ?? null;
  mm.color.set(0xffffff);
  mm.side = alpha.side; mm.transparent = alpha.transparent; mm.opacity = alpha.opacity; mm.alphaTest = alpha.alphaTest; mm.alphaMap = alpha.alphaMap;
  mm.emissive = src.emissive ? src.emissive.clone() : new THREE.Color(1, 1, 1);
  mm.emissiveMap = em;
  mm.emissiveIntensity = FURNITURE_EMISSIVE_GAIN * (src.emissiveIntensity ?? 1);
  m.lights = false;
  m.fog = false;
  furnStats.emissive++;
  return m;
}
/** How strongly a cooked emissive map is added on the unlit clone (decided with pictures in the bed). */
export const FURNITURE_EMISSIVE_GAIN = 0.7;

/**
 * The unlit material a furniture clone wears in place of its cooked PBR one (the interior-materials law: PBR goes
 * black at night indoors). ONE material per SOURCE material per visit — six dining chairs share one — handed out
 * ref-counted: every call is paired with one `dispose()` by its caller exactly as before, and the GPU material is
 * freed when the last holder lets go. (Without a `look` there is no visit to share within: a fresh material.)
 */
export function furnitureMaterial(src: THREE.MeshStandardMaterial, look?: InteriorLook): Unlit {
  if (!interiorLookEnabled()) return legacyFurnitureMaterial(src);
  furnStats.calls++;
  const emissiveOn = INTERIOR_DETAIL_RULES[clampDetail(look?.detail ?? activePreset().interiorDetail)].emissive;
  if (!look) { furnStats.built++; return styledFurnitureMaterial(src, emissiveOn); }
  completeLook(look);
  let per = furnCache.get(look);
  if (!per) { per = new Map(); furnCache.set(look, per); }
  const hit = per.get(src.uuid);
  if (hit) { hit.refs++; return hit.mat; }
  const mat = styledFurnitureMaterial(src, emissiveOn);
  furnStats.built++;
  const entry: SharedMat = { mat, refs: 1 };
  per.set(src.uuid, entry);
  const real = mat.dispose.bind(mat);
  const map = per;
  mat.dispose = (): void => {
    if (--entry.refs > 0) return;
    if (map.get(src.uuid) === entry) map.delete(src.uuid);
    real();
  };
  return mat;
}

// ─── who built this room (rows) ──────────────────────────────────────────────

type Rows<T> = { iter(): Iterable<T> };
interface LookDb {
  world?: Rows<{ worldId: bigint; kind: string; terrainSeed: string }>;
  structure?: { structureId: { find(i: bigint): { posX: number; posZ: number; cityId: bigint; worldId: bigint } | null | undefined } };
  structureMeta?: { structureId: { find(i: bigint): { name: string } | null | undefined } };
  cityAllegiance?: { cityId: { find(i: bigint): { civ: string } | null | undefined } };
  playerCiv?: { civId: { find(i: bigint): { banner: string } | null | undefined } };
  playerCivStyle?: { civId: { find(i: bigint): { tradition: string } | null | undefined } };
}

/**
 * The three small public tables a look is resolved from (who a city stands with, a player civilisation's banner and
 * its chosen tradition). The City console and the ground-war module subscribe two of them for their own windows; the
 * look must not depend on either being registered, so it asks for all three itself — one subscription EACH (a table
 * an older server lacks fails alone). Rows the client already holds cost nothing more.
 */
let civSubsFor: unknown = null;
function ensureCivLookSubs(): void {
  const conn = atlas.conn as unknown as { subscriptionBuilder?: () => { onError(cb: () => void): { subscribe(sql: string[]): void } } } | null;
  if (!conn || typeof conn.subscriptionBuilder !== 'function' || civSubsFor === conn) return;
  civSubsFor = conn;
  for (const q of ['SELECT * FROM city_allegiance', 'SELECT * FROM player_civ', 'SELECT * FROM player_civ_style']) {
    try { conn.subscriptionBuilder().onError(() => { /* an older publish: the look stays neutral */ }).subscribe([q]); } catch { /* a bed, a harness */ }
  }
}
try { bus.on('atlas:account-ready', () => ensureCivLookSubs()); } catch { /* no bus in a bed */ }
/** How far from a settlement's centre a lone deed still builds in that settlement's tradition. */
const NEAR_SETTLEMENT_M: Record<string, number> = { capital: 3600, city: 900, town: 600, hamlet: 400, district: 600, circuit: 600 };

function nearestSettlementCiv(db: LookDb, x: number, z: number): string | null {
  let seed = '';
  for (const w of db.world?.iter() ?? []) if (w.kind === 'commons') { seed = w.terrainSeed; if (atlas.currentWorldId !== null && w.worldId !== atlas.currentWorldId) return null; break; }
  if (!seed) return null;
  let best: { id: number; siteSeed: number } | null = null, bestD = Infinity;
  for (const s of settlementSites(seed)) {
    const d = Math.hypot(s.centerX - x, s.centerZ - z);
    if (d <= (NEAR_SETTLEMENT_M[s.tier] ?? 600) && d < bestD) { bestD = d; best = s; }
  }
  return best ? civOfSettlementRowC(BigInt(best.id + 1), best.siteSeed) : null;
}

/** The builder's allegiance and the player's own name for the building, from the rows the client already holds. */
function builderOf(b: { id: string; key: string; x?: number; z?: number }): { civ: string | null; title?: string; banner?: string | null; civTradition?: Tradition } {
  const pack = packCivOf(b.key);
  if (pack) return { civ: pack }; // a settlement building: its pack IS its construction tradition (it survives a conquest)
  const conn = atlas.conn;
  const db = conn ? (conn.db as unknown as LookDb) : ({} as LookDb); // THE TS2589 TRAP: never `(conn?.db ?? {}) as …`
  const near = (): string | null => (b.x === undefined || b.z === undefined ? null : nearestSettlementCiv(db, b.x, b.z));
  try {
    if (b.id.startsWith('settle:')) return { civ: near() }; // a legacy-named prefab in a town
    if (b.id.startsWith('deed:')) {
      const sid = BigInt(b.id.slice(5));
      const title = (db.structureMeta?.structureId.find(sid)?.name ?? '').trim();
      const row = db.structure?.structureId.find(sid);
      // a deed in a player city wears the city's declared alignment ('a' | 'b' | '' | 'p:<civ>' — a player
      // civilisation has no prescribed colour); a lone deed takes the nearest settlement's civ
      const inCity = !!row && row.cityId !== 0n;
      const al = inCity ? db.cityAllegiance?.cityId.find(row.cityId)?.civ : undefined;
      const civ = al !== undefined ? al : inCity ? '' : near();
      // A PLAYER CIVILISATION (`p:<id>`): the tradition it CHOSE (no row = neutral) and its own banner as the accent
      const pid = playerCivIdOf(civ);
      if (pid !== null) {
        const banner = db.playerCiv?.civId.find(pid)?.banner ?? null;
        const civTradition = asTradition(db.playerCivStyle?.civId.find(pid)?.tradition) ?? 'neutral';
        return title ? { civ, title, banner, civTradition } : { civ, banner, civTradition };
      }
      return title ? { civ, title } : { civ };
    }
  } catch { /* rows mid-churn: the neutral treatment */ }
  return { civ: null }; // hivelot: (the Merchant Hive), preset: (a personal world), mark: (nothing known)
}

/** A binding that knows a building IS a town venue (slice E registers one; none exists today — signs read the purpose). */
let venueResolver: ((look: InteriorLook) => VenueKind | null) | null = null;
export function setInteriorVenueResolver(fn: ((look: InteriorLook) => VenueKind | null) | null): void { venueResolver = fn; }

const completed = new WeakSet<InteriorLook>();
/**
 * Complete a look IN PLACE from the rows — once per look object. The visit fills what the VISIT knows on its one line
 * (id, key, purpose, where the building stands, the shell's size); this adds what the ROWS know (who built it, the
 * player's own name, a bound venue), the seed, and records the room for the audio runtime. `shellMaterials` — the
 * first thing the visit asks for — calls it, so the sign and the furniture adapter see the same completed look.
 * Under the lever nothing is added and no room is recorded (the audio keeps law 26's one generic room).
 */
export function completeLook(look: InteriorLook): InteriorLook {
  if (completed.has(look)) return look;
  completed.add(look);
  ensureCivLookSubs();
  if (!interiorLookEnabled()) { setActiveRoom(null); return look; }
  if (look.civ === undefined && look.tradition === undefined) {
    const who = builderOf(look);
    look.civ = who.civ;
    if (who.title && !look.title) look.title = who.title;
    if (who.banner !== undefined) look.banner = who.banner;
    if (who.civTradition !== undefined) look.civTradition = who.civTradition;
  }
  // THE NARROWER LEVER: with `atlas:interior-civ-look = 'off'` a player civilisation builds neutral with no accent
  if (!civLookEnabled()) { look.banner = null; look.civTradition = null; }
  if (look.seed === undefined) look.seed = seedOf(look.id);
  if (look.venue === undefined) look.venue = venueResolver ? venueResolver(look) : null;
  if (look.hw !== undefined && look.hd !== undefined && look.x !== undefined && look.z !== undefined) {
    const st = styleFor(look);
    const yaw = look.yaw ?? 0, hd = look.hd;
    // the door: building-local (0, −hd) through the visit's own toWorld
    setActiveRoom({
      id: look.id, room: roomFamilyFor(look.purpose, st.tradition, roomVolume(look.hw, hd, look.floors ?? 1)),
      doorX: look.x - hd * Math.sin(yaw), doorZ: look.z - hd * Math.cos(yaw), hw: look.hw, hd, tradition: st.tradition, family: st.family,
    });
  }
  return look;
}

/** The completed look of a building (beds, later slices): the same thing the visit's one line + `shellMaterials` produce. */
export function resolveLook(
  b: { id: string; key: string; x: number; z: number; yaw?: number; groundY: number }, purpose: InteriorPurpose,
  spec?: { floors: number; hw: number; hd: number },
): InteriorLook {
  return completeLook({ id: b.id, key: b.key, purpose, x: b.x, z: b.z, yaw: b.yaw, groundY: b.groundY, floors: spec?.floors, hw: spec?.hw, hd: spec?.hd });
}

/** Bed / probe facts: what the session holds and what the last visit asked for. */
export function interiorLookStats(): Record<string, unknown> {
  let bytes = 0;
  for (const e of canvases.values()) bytes += e.bytes;
  return { enabled: interiorLookEnabled(), canvases: canvases.size, canvasKB: Math.round(bytes / 1024), keys: [...canvases.keys()], lastVisit, furniture: { ...furnStats } };
}
/** `traditionOf` re-exported for callers that only hold a civ. */
export { traditionOf };
// The lever probes and field diagnosis read (THE PROBES-READ-HOOKS law): what the session holds, the room in play.
(globalThis as unknown as { __interiorLook?: () => unknown }).__interiorLook = () => ({ ...interiorLookStats(), room: activeRoom(atlas.interiorId) });
